import React, { useState, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  Chip, IconButton, Tabs, Tab, Collapse, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { fieldDefinitionAdminApi, FieldDefinition } from './fieldDefinitionsAdminApi';
import { colors, typo, tabsSx, tableHeadCellSx, tableCellSx, primaryButtonSx, secondaryButtonSx, dialogActionsSx } from '../../../theme/tokens';
import { PageHeader, SearchField, EmptyState, WriteButton, WriteIconButton } from '../../../components/ui';

/**
 * R7 route-move of the former tenant-scoped Field Definitions page. The
 * component is structurally identical to the original at
 * `src/pages/FieldDefinitions/FieldDefinitions.tsx`; the differences are:
 *  1. All API calls go through `fieldDefinitionAdminApi`, which targets
 *     `/api/admin/field-definitions` (super-admin-gated).
 *  2. There is no tenantCode in scope — JSON field definitions are global
 *     by design (the table has no tenant_code column).
 *
 * Visual polish and feature parity with the legacy page were intentionally
 * left alone this round; only the route move + URL swap is in scope.
 */

const JSON_TYPES = ['completion_criteria', 'recurrence_definition', 'reward_config', 'tenant_attr', 'tenant_option_json', 'adventure_config'];

/**
 * json_types listed here have never been wired to a renderer — nothing reads
 * config.json_field_definition for them, so a row added under one of these does
 * nothing. Tenant attributes have their own definition store (mig 280), managed
 * from MasterTenant → Attributes → Attribute Definitions. The types stay in the
 * list so any rows already created remain visible/removable.
 */
const INERT_JSON_TYPES: Record<string, string> = {
  tenant_attr: 'Tenant attributes are defined in MasterTenant → Attributes → Attribute Definitions. Rows added here are not read by anything.',
  tenant_option_json: 'Nothing reads this type yet — rows added here are not rendered anywhere.',
};
const FIELD_TYPES = ['text', 'number', 'boolean', 'select', 'multitext', 'color', 'url'];

const EMPTY_FORM = {
  json_type: 'completion_criteria', group_name: 'General', group_order: 0,
  field_key: '', field_label: '', field_description: '', field_type: 'text', field_order: 0,
  field_options: '', field_default: '', visible_when_field: '', visible_when_op: 'eq', visible_when_value: '',
};

/** Mini field preview component showing how the field will render */
const FieldPreview: React.FC<{ field: FieldDefinition }> = ({ field }) => {
  const previewLabel = field.field_label || field.field_key;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: '6px', backgroundColor: colors.bgSubtle, border: `1px solid ${colors.borderLight}` }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase' }}>{field.field_type}</Typography>
      <Box sx={{ width: 1, height: 12, backgroundColor: colors.borderLight }} />
      {field.field_type === 'boolean' ? (
        <Box sx={{ width: 24, height: 12, borderRadius: 6, backgroundColor: colors.successBgStrong, border: `1px solid ${colors.successBorder}` }} />
      ) : field.field_type === 'select' ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Typography sx={{ fontSize: 10, color: colors.textSecondary }}>{previewLabel}</Typography>
          <ExpandMoreIcon sx={{ fontSize: 10, color: colors.textTertiary }} />
        </Box>
      ) : field.field_type === 'color' ? (
        <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: typeof field.field_default === 'string' ? field.field_default : colors.brand, border: `1px solid ${colors.borderDefault}` }} />
      ) : (
        <Typography sx={{ fontSize: 10, color: colors.textSecondary, fontStyle: 'italic' }}>{previewLabel}</Typography>
      )}
    </Box>
  );
};

const FieldDefinitionsAdminPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState('completion_criteria');
  const [search, setSearch] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const { data: fields, isLoading } = useQuery({
    queryKey: ['admin-field-definitions', typeFilter],
    queryFn: () => fieldDefinitionAdminApi.getAll(typeFilter || undefined),
  });

  const { data: allFields } = useQuery({
    queryKey: ['admin-field-definitions-all'],
    queryFn: () => fieldDefinitionAdminApi.getAll(undefined),
  });

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    JSON_TYPES.forEach((t) => { counts[t] = 0; });
    (allFields || []).forEach((f: FieldDefinition) => {
      if (counts[f.json_type] !== undefined) counts[f.json_type]++;
    });
    return counts;
  }, [allFields]);

  const groupedFields = useMemo(() => {
    const f = (fields || []).filter((f: FieldDefinition) => !search || f.field_key.toLowerCase().includes(search.toLowerCase()) || f.field_label.toLowerCase().includes(search.toLowerCase()));
    const groups: Record<string, FieldDefinition[]> = {};
    f.forEach((field: FieldDefinition) => {
      const g = field.group_name || 'Ungrouped';
      if (!groups[g]) groups[g] = [];
      groups[g].push(field);
    });
    return Object.entries(groups).sort(([, a], [, b]) => (a[0]?.group_order || 0) - (b[0]?.group_order || 0));
  }, [fields, search]);

  const toggleGroup = (groupName: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) next.delete(groupName); else next.add(groupName);
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      let options = null;
      let defaultVal: unknown = undefined;
      let visibleVal: unknown = undefined;
      try { if (form.field_options) options = JSON.parse(form.field_options); } catch (_) { /* */ }
      try { if (form.field_default) defaultVal = JSON.parse(form.field_default); } catch (_) { defaultVal = form.field_default; }
      try { if (form.visible_when_value) visibleVal = JSON.parse(form.visible_when_value); } catch (_) { visibleVal = form.visible_when_value; }

      const payload = {
        json_type: form.json_type, group_name: form.group_name, group_order: form.group_order,
        field_key: form.field_key, field_label: form.field_label, field_description: form.field_description || null,
        field_type: form.field_type, field_order: form.field_order,
        field_options: options, field_default: defaultVal,
        visible_when_field: form.visible_when_field || null, visible_when_op: form.visible_when_op || null,
        visible_when_value: visibleVal,
      };

      if (editId) await fieldDefinitionAdminApi.update(editId, payload);
      else await fieldDefinitionAdminApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-field-definitions'] });
      setEditOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => fieldDefinitionAdminApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-field-definitions'] }),
  });

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY_FORM, json_type: typeFilter }); setEditOpen(true); };
  const openEdit = (f: FieldDefinition) => {
    setEditId(f.id);
    setForm({
      json_type: f.json_type, group_name: f.group_name, group_order: f.group_order,
      field_key: f.field_key, field_label: f.field_label, field_description: f.field_description || '',
      field_type: f.field_type, field_order: f.field_order,
      field_options: f.field_options ? JSON.stringify(f.field_options, null, 2) : '',
      field_default: f.field_default !== null && f.field_default !== undefined ? JSON.stringify(f.field_default) : '',
      visible_when_field: f.visible_when_field || '', visible_when_op: f.visible_when_op || 'eq',
      visible_when_value: f.visible_when_value !== null && f.visible_when_value !== undefined ? JSON.stringify(f.visible_when_value) : '',
    });
    setEditOpen(true);
  };

  const uf = (f: string, v: unknown) => setForm((p) => ({ ...p, [f]: v }));

  return (
    <Box>
      <PageHeader title="Field Definitions" subtitle="Define fields for structured JSON editors. Each field maps to a key in the JSON config." actions={
        <WriteButton variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={primaryButtonSx}>
          Add Field
        </WriteButton>
      } />

      <Tabs value={typeFilter} onChange={(_, v) => setTypeFilter(v)} variant="scrollable" scrollButtons="auto"
        sx={tabsSx}>
        {JSON_TYPES.map((t) => <Tab key={t} label={`${t} (${typeCounts[t] || 0})`} value={t} />)}
      </Tabs>

      {INERT_JSON_TYPES[typeFilter] && (
        <Alert severity="info" sx={{ mt: 2, mb: 1, fontSize: 13 }}>
          {INERT_JSON_TYPES[typeFilter]}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <SearchField value={search} onChange={setSearch} placeholder="Search fields..." />
      </Box>

      {isLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : groupedFields.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${colors.borderLight}` }}>
          <EmptyState icon={<TuneOutlinedIcon sx={{ fontSize: 48, color: colors.borderDefault }} />} title="No fields defined for this type" description="Add a field definition to customize JSON editor behavior" variant="data" />
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {groupedFields.map(([groupName, groupFields]) => {
            const isCollapsed = collapsedGroups.has(groupName);
            return (
              <Paper key={groupName} elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${colors.borderLight}`, mb: 2, overflow: 'hidden' }}>
                <Box
                  onClick={() => toggleGroup(groupName)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, cursor: 'pointer', backgroundColor: colors.bgSubtle, borderBottom: isCollapsed ? 'none' : `1px solid ${colors.borderLight}`, '&:hover': { backgroundColor: colors.bgMuted } }}>
                  {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 18, color: colors.textSecondary }} /> : <ExpandLessIcon sx={{ fontSize: 18, color: colors.textSecondary }} />}
                  <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'Manrope, sans-serif', color: colors.textPrimary }}>{groupName}</Typography>
                  <Chip size="small" label={`${groupFields.length} field${groupFields.length !== 1 ? 's' : ''}`}
                    sx={{ fontSize: 11, fontWeight: 600, backgroundColor: colors.bgMuted, color: colors.textSecondary }} />
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                    {[...new Set(groupFields.map((f) => f.field_type))].map((ft) => (
                      <Chip key={ft} label={ft} size="small" sx={{ fontSize: 10, height: 20, backgroundColor: colors.infoBgStrong, color: colors.infoText }} />
                    ))}
                  </Box>
                </Box>
                <Collapse in={!isCollapsed}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={tableHeadCellSx}>Field Key</TableCell>
                          <TableCell sx={tableHeadCellSx}>Label</TableCell>
                          <TableCell sx={tableHeadCellSx}>Type</TableCell>
                          <TableCell sx={tableHeadCellSx}>Preview</TableCell>
                          <TableCell sx={tableHeadCellSx}>Visible When</TableCell>
                          <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupFields.map((f) => (
                          <TableRow key={f.id} hover sx={{ '&:hover': { backgroundColor: colors.brandSubtle } }}>
                            <TableCell sx={{ ...tableCellSx, ...typo.mono }}>{f.field_key}</TableCell>
                            <TableCell sx={tableCellSx}>{f.field_label}</TableCell>
                            <TableCell sx={tableCellSx}><Chip size="small" label={f.field_type} sx={{ fontSize: 12 }} /></TableCell>
                            <TableCell sx={tableCellSx}><FieldPreview field={f} /></TableCell>
                            <TableCell sx={{ ...tableCellSx, color: colors.textTertiary, fontSize: 12 }}>{f.visible_when_field ? `${f.visible_when_field} ${f.visible_when_op} ${JSON.stringify(f.visible_when_value)}` : '—'}</TableCell>
                            <TableCell sx={tableCellSx}>
                              <WriteIconButton size="small" onClick={() => openEdit(f)}><EditIcon sx={{ fontSize: 16 }} /></WriteIconButton>
                              <WriteIconButton size="small" onClick={() => { if (window.confirm('Delete this field?')) deleteMutation.mutate(f.id); }}><DeleteIcon sx={{ fontSize: 16, color: colors.errorIcon }} /></WriteIconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Edit Field' : 'Add Field'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>JSON Type *</Typography>
                <Select fullWidth size="small" value={form.json_type} onChange={(e) => uf('json_type', e.target.value)} sx={{ fontSize: 14 }}>
                  {JSON_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </Box>
              <TextField label="Group Name *" size="small" sx={{ flex: 1 }} value={form.group_name} onChange={(e) => uf('group_name', e.target.value)} />
              <TextField label="Group Order" size="small" type="number" sx={{ width: 100 }} value={form.group_order} onChange={(e) => uf('group_order', parseInt(e.target.value, 10))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Field Key *" size="small" sx={{ flex: 1 }} value={form.field_key} onChange={(e) => uf('field_key', e.target.value)} helperText="Dot-path e.g. healthCriteria.requiredSteps" />
              <TextField label="Field Label *" size="small" sx={{ flex: 1 }} value={form.field_label} onChange={(e) => uf('field_label', e.target.value)} />
              <TextField label="Order" size="small" type="number" sx={{ width: 80 }} value={form.field_order} onChange={(e) => uf('field_order', parseInt(e.target.value, 10))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Field Type *</Typography>
                <Select fullWidth size="small" value={form.field_type} onChange={(e) => uf('field_type', e.target.value)} sx={{ fontSize: 14 }}>
                  {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </Box>
              <TextField label="Description" size="small" sx={{ flex: 2 }} value={form.field_description} onChange={(e) => uf('field_description', e.target.value)} />
            </Box>
            <TextField label="Options JSON (for select)" size="small" multiline minRows={2} value={form.field_options} onChange={(e) => uf('field_options', e.target.value)}
              helperText='[{"label":"Steps","value":"Steps"}]' InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
            <TextField label="Default Value (JSON)" size="small" value={form.field_default} onChange={(e) => uf('field_default', e.target.value)}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
            <Typography sx={{ ...typo.sectionLabel, mt: 1 }}>Conditional Visibility</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Visible When Field" size="small" sx={{ flex: 1 }} value={form.visible_when_field} onChange={(e) => uf('visible_when_field', e.target.value)} helperText="Dot-path of controlling field" />
              <Box sx={{ width: 100 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>Op</Typography>
                <Select fullWidth size="small" value={form.visible_when_op} onChange={(e) => uf('visible_when_op', e.target.value)} sx={{ fontSize: 14 }}>
                  <MenuItem value="eq">eq</MenuItem>
                  <MenuItem value="neq">neq</MenuItem>
                  <MenuItem value="truthy">truthy</MenuItem>
                  <MenuItem value="falsy">falsy</MenuItem>
                </Select>
              </Box>
              <TextField label="Value (JSON)" size="small" sx={{ flex: 1 }} value={form.visible_when_value} onChange={(e) => uf('visible_when_value', e.target.value)}
                helperText='"HEALTH" or true' InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setEditOpen(false)} sx={secondaryButtonSx}>Cancel</Button>
          <WriteButton variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.field_key || !form.field_label || saveMutation.isPending}
            sx={primaryButtonSx}>
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </WriteButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldDefinitionsAdminPage;
